import {
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  IconButton,
} from '@mui/material'
import BuildIcon from '@mui/icons-material/Build'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCodeValidationRule,
  deselectCodeValidationRule,
  selectCodeValidationRuleElement,
} from '../../actions/validation'
import {
  CodeDetail,
  CodeDetailsListProps,
  CodeValidationDiscipline,
  CodeValidationStatus,
} from '../../models/Validation'

const statusColor = (status: CodeValidationStatus) =>
  status === CodeValidationStatus.Passed ? '#00C961' : '#FF2F5F'

const IndividualCodeDetailList = ({ codes }: CodeDetailsListProps) => {
  const dispatch = useDispatch()
  const selectedRule = useSelector(
    (state: any) => state.validation.selectedCodeValidationRule,
  )

  const handleClick = (item: CodeDetail) => {
    if (selectedRule && selectedRule.code === item.codeReference) {
      dispatch(deselectCodeValidationRule())
    } else {
      dispatch(
        selectCodeValidationRule({
          item: {
            id: item.codeReference,
            codeId: item.codeReference,
            codeReference: '',
            elements: item.elements || [],
            discipline:
              (item as any).discipline || CodeValidationDiscipline.Other,
            description: item.description,
            atomics: [],
          },
        }),
      )
      dispatch(
        selectCodeValidationRuleElement({
          item: null,
        }),
      )
    }
  }

  return (
    <Box sx={{ height: '85%', position: 'relative' }}>
      <Box
        sx={{
          height: '100%',
          maxHeight: '60vh',
          width: '100%',
        }}
      >
        <List sx={{ height: '100%' }} dense>
          {codes.map((code, i) => {
            const allPassed =
              code.elements?.length > 0 &&
              code.elements.every(
                (el) => el.status === CodeValidationStatus.Passed,
              )

            const status = allPassed
              ? CodeValidationStatus.Passed
              : CodeValidationStatus.Failed
            return (
              <ListItem
                key={code.codeReference + i}
                onClick={() => handleClick(code)}
                sx={{
                  borderBottom: '1px solid #eee',
                  py: 0,
                  pl: 0,
                  backgroundColor: '#F5F5F5',
                  mb: 1,
                  height: '41px',
                  '&:hover .fix-btn': { display: 'inline-flex' },
                  '&:hover .locate-btn': { display: 'inline-flex' },
                }}
                disableGutters
              >
                <Box
                  display='flex'
                  alignItems='center'
                  width='100%'
                  height='100%'
                >
                  {/* Left color bar based on code status */}
                  <Box
                    sx={{
                      width: 9,
                      height: 38,
                      backgroundColor: statusColor(status),
                      borderTopLeftRadius: 4,
                      borderBottomLeftRadius: 4,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      marginRight: 1,
                      alignSelf: 'center',
                      boxShadow: '2px 0 6px 0 rgba(0,0,0,0.07)',
                      clipPath: 'inset(0 50% 0 0)', // show only left half horizontally
                    }}
                  />
                  {/* Code column (left) */}
                  <Box
                    width={50}
                    display='flex'
                    alignItems='center'
                    justifyContent='flex-start'
                  >
                    <Typography fontSize={10} color='#888' noWrap>
                      {code.codeReference}
                    </Typography>
                  </Box>
                  {/* Description column */}
                  <Box flex={2} minWidth={0}>
                    <Typography
                      fontWeight={500}
                      fontSize={12}
                      color='#04002E'
                      noWrap
                      title={code.description}
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: 180,
                      }}
                    >
                      {code.description.length > 32
                        ? `${code.description.slice(0, 32)}...`
                        : code.description}
                    </Typography>
                  </Box>
                  {/* Status column */}
                  <Box
                    flex={1}
                    minWidth={0}
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <Chip
                      label={status.toUpperCase()}
                      size='small'
                      sx={{
                        fontWeight: 600,
                        color: statusColor(status),
                        border: `1px solid ${statusColor(status)}`,
                        backgroundColor: 'transparent',
                        borderRadius: '4px',
                        height: 22,
                        minWidth: 55,
                        px: 0,
                        '& .MuiChip-label': { px: 0 },
                      }}
                    />
                  </Box>
                  {/* Locate button in its own column, always shown on hover */}
                  <Box
                    minWidth={120}
                    display='flex'
                    alignItems='center'
                    justifyContent='flex-end'
                    gap={1}
                  >
                    <IconButton
                      className='locate-btn'
                      size='small'
                      sx={{
                        display: 'none',
                        p: 0.5,
                        backgroundColor: '#fff',
                        border: '1px solid #3F69FF',
                        borderRadius: '3px',
                        color: '#3F69FF',
                        '&:hover': { backgroundColor: '#e6edff' },
                      }}
                      aria-label='locate'
                    >
                      <Typography
                        fontSize={11}
                        fontWeight={600}
                        color='#3F69FF'
                      >
                        LOCATE
                      </Typography>
                    </IconButton>
                    {status === CodeValidationStatus.Failed && (
                      <IconButton
                        className='fix-btn'
                        size='small'
                        sx={{
                          display: 'none',
                          p: 0.5,
                          backgroundColor: '#3F69FF',
                          borderRadius: '3px',
                          '&:hover': { backgroundColor: '#3356cc' },
                        }}
                        aria-label='fix'
                      >
                        <BuildIcon fontSize='small' sx={{ color: '#fff' }} />
                        <Typography
                          fontSize={11}
                          fontWeight={600}
                          ml={0.5}
                          color='#fff'
                        >
                          FIX
                        </Typography>
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </Box>
  )
}

export default IndividualCodeDetailList
